// The station is a shared clock, not a per-visitor playlist. Everyone who opens
// the page at the same moment lands in the same second of the same song, which
// is the whole difference between a radio station and a shuffle button - and it
// costs one modulo rather than a streaming server.
//
// The order is reshuffled once a day so a small library doesn't feel like a
// loop, and derived from the date so every client computes the same order
// without asking the server for it.

// Small deterministic PRNG. Any seeded generator works; this one is short and
// has no dependency.
function mulberry32(seed) {
  let a = seed;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export function dayKey(at) {
  return Math.floor(at.getTime() / 86400000);
}

// Fisher-Yates against the seeded stream.
export function playlistFor(tracks, seed) {
  const shuffled = tracks.slice();
  const random = mulberry32(seed);

  for (let i = shuffled.length - 1; i > 0; i -= 1) {
    const j = Math.floor(random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }

  return shuffled;
}

// Where the station is right now: which track, and how far into it.
//
// Returns null for an empty library rather than throwing, so the page can render
// its empty state.
export function nowPlaying(playlist, at) {
  if (!playlist.length) return null;

  const total = playlist.reduce((sum, track) => sum + track.durationMs, 0);
  if (!total) return null;

  // Elapsed since UTC midnight, which is also when the order last changed.
  let remainder = (at.getTime() - dayKey(at) * 86400000) % total;

  for (let index = 0; index < playlist.length; index += 1) {
    if (remainder < playlist[index].durationMs) {
      return { index, offsetMs: remainder, track: playlist[index] };
    }
    remainder -= playlist[index].durationMs;
  }

  // Unreachable unless a duration is NaN; fail to the top of the hour.
  return { index: 0, offsetMs: 0, track: playlist[0] };
}

// The active line, plus the lines either side of it for context. Lines are in
// order, so this is a scan rather than a search - the lists are short enough
// that binary search would be noise.
export function activeLineIndex(lines, positionMs) {
  if (!lines.length) return -1;

  let active = -1;
  for (let index = 0; index < lines.length; index += 1) {
    if (lines[index].startMs > positionMs) break;
    active = index;
  }

  return active;
}
